/**
 * Experimental zoom-call auto-framer.
 *
 * Splits a continuous recording into one scene per speaker turn and frames
 * each scene tightly around the active speaker's face in a 16:9 sub-region
 * within the 9:16 frame (`TemplateIds.ZoomCall`).
 *
 * When the active speaker's face isn't visible during a turn (no mapping
 * or no matching detections), the scene is still created — without a
 * per-scene crop override, so the renderer falls back to the layout's
 * default crop. See plan: PLAT-6474 zoom-call experimental.
 */

import { AbstractCommand } from '@src/editor/commands/AbstractCommand';
import { Scene } from '@src/editor/models/layout';
import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { LayoutSegmentFactory } from '@src/editor/models/layout/LayoutSegmentFactory';
import { TemplateIds } from '@src/editor/models/layout/presetTemplates';
import { EditorChangeType } from '@src/editor/types';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import { SceneService } from '@src/services/SceneService';

import { buildSpeakerTurns, type SpeakerTurn } from './buildSpeakerTurns';
import {
  computeCropForFaceWithMarginAndAR,
  type CropBox,
} from './computeCropForFaceWithMarginAndAR';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { FaceDetection } from '@src/services/framing/types';

const ZOOM_CALL_TARGET_AR = 16 / 9;
const DEFAULT_MARGIN_FRACTION = 0.15;

export interface CreateScenesAndAutoframeZoomCallIn9by16Input {
  editor: IVideoEditor;
  faceDetections: FaceDetection[];
  speakerLabelToFaceLabels: Record<string, string[]>;
  /** Keyed by `VideoElement.source` (URL). */
  naturalMediaDimensions: Record<string, NaturalDimensions>;
  /** Fraction of face height to use as margin above and below. Default 0.15. */
  marginFraction?: number;
}

export function createScenesAndAutoframeZoomCallIn9by16(
  input: CreateScenesAndAutoframeZoomCallIn9by16Input,
): boolean {
  const command = new CreateScenesAndAutoframeZoomCallCommand(input);
  return input.editor.executeCommand(command);
}

class CreateScenesAndAutoframeZoomCallCommand extends AbstractCommand {
  private readonly faceDetections: FaceDetection[];
  private readonly speakerLabelToFaceLabels: Record<string, string[]>;
  private readonly naturalMediaDimensions: Record<string, NaturalDimensions>;
  private readonly marginFraction: number;
  private readonly sceneService = new SceneService();
  private framingService: SmartFramingService | null = null;
  private previousScenes: Scene[] = [];
  private newScenes: Scene[] = [];

  constructor({
    editor,
    faceDetections,
    speakerLabelToFaceLabels,
    naturalMediaDimensions,
    marginFraction = DEFAULT_MARGIN_FRACTION,
  }: CreateScenesAndAutoframeZoomCallIn9by16Input) {
    super(editor as VideoEditor);
    this.faceDetections = faceDetections;
    this.speakerLabelToFaceLabels = speakerLabelToFaceLabels;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.marginFraction = marginFraction;
  }

  getName(): string {
    return 'CreateScenesAndAutoframeZoomCallIn9by16';
  }

  getDescription(): string {
    return 'Frame as Zoom Call';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  execute(): boolean {
    const transcript = this.editor.getOriginalTranscript();
    if (!transcript || transcript.length === 0) return false;

    const turns = buildSpeakerTurns(transcript);
    if (turns.length === 0) return false;

    const transcriptTrack = this.editor.getVideoTranscriptTrack();
    if (!transcriptTrack) return false;

    const elementBounds = transcriptTrack.elements.map(el => ({
      element: el as VideoTranscriptElement,
      mediaStart: this.sceneService.getElementMediaStartTime(el),
      mediaEnd: this.sceneService.getElementMediaEndTime(el),
    }));

    this.previousScenes = [...this.editor.timeline.scenes];

    const scenes: Scene[] = [];
    for (const turn of turns) {
      const scene = this.buildSceneForTurn(
        turn,
        elementBounds,
        transcriptTrack.id,
      );
      if (scene) scenes.push(scene);
    }

    if (scenes.length === 0) return false;

    this.newScenes = scenes;
    this.sceneService.replaceScenes(this.editor.timeline, scenes);
    this.sceneService.validateAndAdjustScenes(this.editor.timeline);
    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_ADDED, {
      sceneId: 'multiple',
      count: scenes.length,
    });
    return true;
  }

  undo(): boolean {
    this.sceneService.replaceScenes(this.editor.timeline, this.previousScenes);
    this.editor.markDirty();
    this.editor.notifyObservers(EditorChangeType.SCENE_REMOVED, {
      sceneId: 'multiple',
      count: this.newScenes.length,
    });
    return true;
  }

  redo(): boolean {
    return this.execute();
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private buildSceneForTurn(
    turn: SpeakerTurn,
    elementBounds: Array<{
      element: VideoTranscriptElement;
      mediaStart: number;
      mediaEnd: number;
    }>,
    transcriptTrackId: string,
  ): Scene | null {
    // v1: anchor to the first transcript element that contains the turn's
    // start time. Continuous zoom recordings have a single element, so
    // this is exact; cut/edited timelines lose any portion of a turn that
    // spills past the element boundary (acceptable for the experimental cut).
    const eb = elementBounds.find(
      b => turn.startTime >= b.mediaStart && turn.startTime < b.mediaEnd,
    );
    if (!eb) return null;

    const clampedEnd = Math.min(turn.endTime, eb.mediaEnd);
    if (clampedEnd <= turn.startTime) return null;

    const anchorStart = this.sceneService.createAnchorForMediaTime(
      eb.element,
      turn.startTime,
      eb.mediaStart,
      eb.mediaEnd,
    );
    const anchorEnd = this.sceneService.createAnchorForMediaTime(
      eb.element,
      clampedEnd,
      eb.mediaStart,
      eb.mediaEnd,
    );
    if (!anchorStart || !anchorEnd) return null;

    const crop = this.computeCropForTurn(turn, eb.element);

    const layout = LayoutSegmentFactory.create(
      TemplateIds.ZoomCall,
      transcriptTrackId,
      crop,
    );

    return new Scene({ anchorStart, anchorEnd, layout });
  }

  private computeCropForTurn(
    turn: SpeakerTurn,
    element: VideoTranscriptElement,
  ): CropBox | undefined {
    const faceLabels = this.speakerLabelToFaceLabels[turn.speakerLabel];
    if (!faceLabels?.length) return undefined;

    const turnDetections = this.faceDetections.filter(
      d =>
        faceLabels.includes(d.label) &&
        d.time >= turn.startTime &&
        d.time < turn.endTime,
    );
    if (turnDetections.length === 0) return undefined;

    const framingService = this.getFramingService();
    const clusters = framingService.clusterFaceBoxes(turnDetections);
    if (clusters.length === 0) return undefined;

    const largest = clusters.reduce((a, b) =>
      a.boxes.length >= b.boxes.length ? a : b,
    );

    const naturalDimensions = this.naturalMediaDimensions[element.source];
    if (!naturalDimensions) return undefined;

    return computeCropForFaceWithMarginAndAR({
      cluster: largest,
      naturalDimensions,
      targetAspectRatio: ZOOM_CALL_TARGET_AR,
      marginFraction: this.marginFraction,
    });
  }

  private getFramingService(): SmartFramingService {
    if (!this.framingService) {
      this.framingService = new SmartFramingService(
        this.editor,
        this.sceneService,
        new LayoutEngine(),
      );
    }
    return this.framingService;
  }
}
